/**
 * Enterprise Login Form
 * Форма входа в систему с валидацией и обработкой ошибок
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { logger } from "../../utils/logger";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { createLoginSchema, extractZodErrors } from "../../utils/validators";
import { consumeReturnUrl } from "../../utils/return-url";
import { MfaChallengeModal } from "./MfaChallengeModal";
import { SsoLoginButton } from "./SsoLoginButton";

interface LocationState {
  from?: { pathname: string };
  error?: string;
}

export default function LoginForm() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    isAuthenticated,
    isLoading,
    error,
    clearError,
    mfaChallenge,
    cancelMfaChallenge,
  } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const state = location.state as LocationState | null;
  const from = state?.from?.pathname || "/dashboard";
  const authError = state?.error || error;

  // Если пользователь аутентифицирован — перенаправляем. Приоритет возврата:
  // 1) sessionStorage.returnUrl (гость пришёл с публичной страницы, например
  //    регистрации на событие /join/:token — см. utils/return-url.ts);
  // 2) location.state.from (классический guard-паттерн ProtectedRoute);
  // 3) /dashboard.
  useEffect(() => {
    if (isAuthenticated) {
      logger.debug("LoginForm: Пользователь аутентифицирован, перенаправляем");
      const returnUrl = consumeReturnUrl();
      navigate(returnUrl ?? from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // При открытии страницы входа сбрасываем предыдущее сообщение об ошибке (например «Сессия истекла»)
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Очистка ошибок при размонтировании
  useEffect(() => {
    return () => {
      if (error) {
        clearError();
      }
    };
  }, [error, clearError]);

  const validateForm = (): boolean => {
    // Схема собирается здесь, а не константой модуля (AD-44): её сообщения —
    // обычные строки, вычисленные в момент сборки, и константа уровня модуля
    // зафиксировала бы язык, действовавший при первом импорте файла. Сборка на
    // каждой отправке стоит одного вызова по клику, зато текст всегда на текущем языке.
    const result = createLoginSchema(t).safeParse(formData);
    if (!result.success) {
      setFormErrors(extractZodErrors(result.error));
      return false;
    }
    setFormErrors({});
    return true;
  };

  /**
   * Обработка отправки формы
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      logger.debug("LoginForm: Отправляем форму входа");
      clearError();

      await login({
        email: formData.email.trim().toLowerCase(),
        password: formData["password"],
      });

      logger.debug("LoginForm: Вход успешен, перенаправляем");
      // Навигация будет выполнена через useEffect когда isAuthenticated станет true
    } catch (error) {
      logger.error("LoginForm: Ошибка входа", error);
      // Ошибка уже обработана в AuthContext
    }
  };

  /**
   * Обработка изменения полей формы
   */
  const handleInputChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));

      // Очищаем ошибку поля при изменении
      if (formErrors[field]) {
        setFormErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom>
            {t("auth.loginTitle")}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 3 }}
          >
            {t("auth.loginSubtitle")}
          </Typography>

          {authError && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {authError}
            </Alert>
          )}

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 1, width: "100%" }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t("auth.email")}
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleInputChange("email")}
              error={!!formErrors["email"]}
              helperText={formErrors["email"]}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t("auth.password")}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={formData["password"]}
              onChange={handleInputChange("password")}
              error={!!formErrors["password"]}
              helperText={formErrors["password"]}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? t("auth.signingIn") : t("auth.signIn")}
            </Button>

            {/* Корпоративный SSO-вход: кнопка сама скрывается, если активных
                SSO-конфигураций нет (см. SsoLoginButton — return null).
                returnTo доносит state.from (ProtectedRoute) через SSO-round-trip. */}
            <SsoLoginButton returnTo={state?.from?.pathname} />

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <RouterLink
                to="/auth/forgot-password"
                style={{ textDecoration: "none" }}
              >
                <Typography variant="body2" color="primary">
                  {t("auth.forgotPassword")}
                </Typography>
              </RouterLink>
            </Box>

            <Box sx={{ textAlign: "center", mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t("auth.noAccount")}{" "}
                {/* state.from пробрасывается в регистрацию: свежий аккаунт после
                    «Продолжить» вернётся туда, куда шёл гость (см. RegisterForm). */}
                <RouterLink
                  to="/auth/register"
                  state={state?.from ? { from: state.from } : null}
                  style={{ textDecoration: "none" }}
                >
                  <Typography component="span" variant="body2" color="primary">
                    {t("auth.register")}
                  </Typography>
                </RouterLink>
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Удалены dev-подсказки с тестовыми данными по требованию: без хардкода */}
      </Box>

      {/* FIX-11 (П0.1): запрашивается, когда login() вернул requiresMfa: true.
          Успешная проверка кода выпускает полноценные токены — дальнейшая
          навигация выполняется через useEffect выше, реагирующий на isAuthenticated. */}
      <MfaChallengeModal
        open={!!mfaChallenge}
        onSuccess={() => {}}
        onCancel={cancelMfaChallenge}
      />
    </Container>
  );
}
